import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import * as R from "ramda";
import { compose, withHandlers } from "recompose";
import { connect } from "react-redux";
import { Row, Col, Input, Form, Tag, Tooltip, Icon, Switch } from "antd";
import { FormItem } from "@worcket/common/components/controls";
import { Content } from "@worcket/common/components/layout";
import { isLoading, withForm } from "@worcket/common/hocs";

import * as gtag from "../../../../lib/gtag";
import Page from "../../../../components/display/Page";
import { getDefaultOfferLetterTemplate } from "../../selectors";
import { isAdmin as isUserAdmin } from "../../../../store/selectors/user";
import {
  updateOpportunity,
  UPDATE_OPPORTUNITY
} from "../../../../store/actions/opportunity";
import JobOfferEditor from "../../../../components/inputs/JobOfferEditor";
import Button from "../../../../components/Button";

import styled from "styled-components/macro";

const ConfigContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  form {
    width: 100%;
  }
`;

const TagsWrapper = styled.div`
  display: flex;
  align-items: center;

  .ant-tag {
    cursor: pointer;
  }

  .title {
    display: inline-block;
    margin-right: 8px;
    margin-bottom: 0;
  }
`;

const Tags = ({ t, title, isDisabled, onTagClick }) => (
  <TagsWrapper>
    <h5 className="title">{title}</h5>
    {R.map(tag => (
      <Tag
        onClick={() => {
          if (isDisabled) return;
          onTagClick(tag);
        }}
        key={tag}
      >
        {tag}
      </Tag>
    ))(["[Nombre del Candidato]", "[Título de la Oportunidad]"])}
    <Tooltip
      title={t("opportunity:process.stepDetails.availableTagsDescription")}
    >
      <Icon type="question-circle" />
    </Tooltip>
  </TagsWrapper>
);

const Title = styled.h1`
  margin: 0;
  width: 100%;
`;

const LabelContainer = styled.div`
  display: inline-flex;
  align-items: center;

  .anticon {
    margin-left: 5px;
  }
`;

const OpportunityConfig = ({
  onSubmit,
  form,
  loading,
  t,
  insertTagInMessage,
  opportunityId,
  permissions,
  isAdmin,
  isOwner,
  isGlobalAdmin
}) => {
  const isDisabled = !isAdmin && !isOwner && !R.includes("write", permissions);
  const closedMessageRef = React.useRef();
  const unmatchMessageRef = React.useRef();

  useEffect(() => {
    gtag.event({
      category: "VacancyConfig",
      action: "VacancyConfig",
      label: opportunityId
    });
  }, [opportunityId]);

  return (
    <Page>
      <Content>
        <ConfigContainer>
          <Title>{t("opportunity:config.title")}</Title>
          <Form onSubmit={onSubmit}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <FormItem
                  form={form}
                  label={
                    <LabelContainer>
                      {t("opportunity:config.closedMessage.title")}
                      <Tooltip
                        title={t(
                          "opportunity:config.closedMessage.description"
                        )}
                      >
                        <Icon type="question-circle" />
                      </Tooltip>
                    </LabelContainer>
                  }
                  name="closedMessage"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t("form:emptyFieldValidation")
                    }
                  ]}
                  render={
                    <Input.TextArea
                      ref={closedMessageRef}
                      disabled={isDisabled || loading}
                      autoSize={{ minRows: 6 }}
                    />
                  }
                />
                <Tags
                  t={t}
                  isDisabled={isDisabled}
                  title={t("opportunity:process.stepDetails.availableTags")}
                  onTagClick={tag => insertTagInMessage(closedMessageRef, tag)}
                />
              </Col>
              <Col xs={24} md={12}>
                <FormItem
                  form={form}
                  label={
                    <LabelContainer>
                      {t("opportunity:config.unmatchMessage.title")}
                      <Tooltip
                        title={t(
                          "opportunity:config.unmatchMessage.description"
                        )}
                      >
                        <Icon type="question-circle" />
                      </Tooltip>
                    </LabelContainer>
                  }
                  name="unmatchMessage"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t("form:emptyFieldValidation")
                    }
                  ]}
                  render={
                    <Input.TextArea
                      disabled={isDisabled || loading}
                      autoSize={{ minRows: 6 }}
                      ref={unmatchMessageRef}
                    />
                  }
                />
                <Tags
                  t={t}
                  isDisabled={isDisabled}
                  title={t("opportunity:process.stepDetails.availableTags")}
                  onTagClick={tag => insertTagInMessage(unmatchMessageRef, tag)}
                />
              </Col>
            </Row>
            <Row style={{ marginTop: 30 }}>
              <Col>
                <FormItem
                  form={form}
                  label={t("opportunity:config.offerLetter.title")}
                  name="offerLetterTemplate"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t("form:emptyFieldValidation")
                    }
                  ]}
                  render={<JobOfferEditor isDisabled={isDisabled} t={t} />}
                />
              </Col>
            </Row>
            {isGlobalAdmin && (
              <section data-test="matching-configuration">
                <h2>{t("opportunity:config.matching.title")}</h2>
                <FormItem
                  form={form}
                  label={t(
                    "opportunity:config.matching.autoMatchOnRequirements"
                  )}
                  name="autoMatchOnRequirements"
                  decoratorProps={{
                    valuePropName: "checked",
                    initialValue: false
                  }}
                  render={<Switch disabled={loading} />}
                />
                <p>{t("opportunity:config.matching.description")}</p>
              </section>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 24,
                width: "100%"
              }}
            >
              <Button
                loading={loading}
                disabled={isDisabled}
                type="primary"
                size="lg"
                htmlType="submit"
              >
                {t("global:save")}
              </Button>
            </div>
          </Form>
        </ConfigContainer>
      </Content>
    </Page>
  );
};

const enhancer = compose(
  withTranslation(["global", "opportunity", "form"]),
  isLoading(UPDATE_OPPORTUNITY),
  connect(
    state => {
      const opportunityConfig = R.pathOr(
        {},
        ["selected", "config"],
        state.opportunity
      );

      return {
        opportunityId: state.opportunity.selected.id,
        companyId: state.company.id,
        company: state.company,
        isGlobalAdmin: isUserAdmin(state.user),
        autoMatchOnRequirements: R.propOr(
          false,
          "autoMatchOnRequirements",
          state.opportunity.selected
        ),
        closedMessage: R.prop("closedMessage", opportunityConfig),
        unmatchMessage: R.prop("unmatchMessage", opportunityConfig),
        offerLetterTemplate: R.prop("offerLetterTemplate", opportunityConfig)
      };
    },
    { updateOpportunity }
  ),
  withForm({
    selector: (
      _,
      {
        company,
        closedMessage,
        unmatchMessage,
        offerLetterTemplate,
        autoMatchOnRequirements,
        isGlobalAdmin
      }
    ) => ({
      closedMessage,
      unmatchMessage,
      ...(isGlobalAdmin
        ? { autoMatchOnRequirements: Boolean(autoMatchOnRequirements) }
        : {}),
      offerLetterTemplate:
        offerLetterTemplate || getDefaultOfferLetterTemplate(company.logo)
    }),
    onSubmit: (values, { updateOpportunity, opportunityId, companyId }) => {
      updateOpportunity(opportunityId, companyId, values);
    }
  }),
  withHandlers({
    insertTagInMessage: ({ form }) => (inputRef, tag) => {
      if (!inputRef || !inputRef.current) {
        return;
      }

      const id = inputRef.current.props.id;
      const message = form.getFieldValue(id);
      const textArea = inputRef.current.resizableTextArea.textArea;
      const { selectionStart, selectionEnd } = textArea;

      form.setFieldsValue(
        {
          [id]:
            message.slice(0, selectionStart) + tag + message.slice(selectionEnd)
        },
        () => {
          const newCursorPosition = selectionStart + tag.length;
          textArea.focus();
          textArea.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      );
    }
  })
);

export default enhancer(OpportunityConfig);
export { OpportunityConfig };
